import * as React from 'react';
import { Dispatch } from 'redux';
import { Translate } from 'react-redux-i18n';
import { DndProvider } from 'react-dnd';
import HTML5Backend from 'react-dnd-html5-backend';
import TouchBackend from 'react-dnd-touch-backend';
import MultiBackend, { TouchTransition } from 'react-dnd-multi-backend';

import { StoreState } from '../../reducers';
import { DriveItemInfo } from '../../containers/DriveItemList/reducer';
import { ListDisplayType, DRIVE_ITEM_LIST_PAGE_SIZE } from '../../containers/DriveItemList/constants';
import { fetchDriveItem, toggleEditMode, getCustomTemplatesIds } from '../../containers/DriveItemList/actions';
import { Api } from '../../typings/api';
import Utils from '../../utils';
import DriveItem from '../../containers/DriveItem';
import {
	StyledDriveItemList,
	StyledInfiniteScroll,
	DriveItemListContainer,
	LoadingStatus,
	TitleWrapper,
	ButtonsWrapper,
	ButtonEdit,
	Loader,
} from './styles';
import Masonry from '../CustomMasonry';
import { AbstractDriveItemListPageParams } from '../../containers/DriveItemListPage/abstract';
import Title from '../../containers/Title';
import DesktopDragPreview from './DesktopDragPreview';
import TouchDragPreview from './TouchDragPreview';
import DragScroller, { DragScrollerPosition } from './DragScroller';
import FilesToast from '../../containers/ImportDesktopFile/Toast';
import FilesDndUploader from '../../containers/ImportDesktopFile/FilesDndUploader';
import UserAgentUtils from '../../utils/userAgent';
import CoverFlow from '../CoverFlow';
import DisplayTypeDropdown from './DisplayTypeDropdown';

interface DriveItemListProps {
	dispatch: Dispatch<StoreState>;
	hasBottomBar: boolean;
	params?: AbstractDriveItemListPageParams;
	displayType: ListDisplayType;
	storeDriveItems: { [k: number]: DriveItemInfo };
	navigationItems: JSX.Element[];
	driveItemIdsToDisplay: number[];
	isFetching: boolean;
	folderId?: number;
	showUnreadBadge: boolean;
	title?: string;
	showSearchField: boolean;
	hasActions?: boolean;
	hasSelectionIcon?: boolean;
	onDriveItemClick?: (item: Api.DriveItem) => void;
	sharedDirId: number | undefined;
	isEditMode: boolean;
	isEditButtonDisplayed: boolean;
	isPrivateDir: boolean;
	isLoading: boolean;
	isFilesDndEnabled?: boolean;
	shouldDisplayFilesToast?: boolean;
	isDisplayTypeDropdownDisplayed?: boolean;
	shouldResetPage?: boolean;
}

interface DriveItemListState {
	page: number;
}

const HTML5toTouch = {
	backends: [
		{
			backend: HTML5Backend,
		},
		{
			backend: TouchBackend,
			preview: true,
			transition: TouchTransition,
		},
	],
};

class DriveItemList extends React.Component<DriveItemListProps, DriveItemListState> {

	private driveItemListElement: HTMLElement;

	constructor(props: DriveItemListProps) {
		super(props);
		this.hasMoreDriveItems = this.hasMoreDriveItems.bind(this);
		this.loadMoreDriveItems = this.loadMoreDriveItems.bind(this);
		this.handleCoverFlowFolderChange = this.handleCoverFlowFolderChange.bind(this);
		this.handleCoverFlowSlideChange = this.handleCoverFlowSlideChange.bind(this);
		this.state = { page: 0 };
	}

	componentDidMount() {
		this.props.dispatch(getCustomTemplatesIds());
	}

	componentDidUpdate(prevProps: DriveItemListProps) {
		const {
			driveItemIdsToDisplay,
			folderId,
			displayType,
			storeDriveItems,
			params,
			shouldResetPage,
			dispatch,
		} = this.props;

		const isUrlChanged = prevProps.params && params && (
			prevProps.params.origin !== params.origin ||
			prevProps.params.originParam !== params.originParam);
		const isDisplayTypeChanged = prevProps.displayType !== displayType;
		const isResetNeeded = !prevProps.shouldResetPage && shouldResetPage;

		if (isUrlChanged || isDisplayTypeChanged || isResetNeeded) {
			this.setState({ page: 0 });
			this.resetEditMode();

			if (this.driveItemListElement) {
				this.driveItemListElement.scrollTop = 0;
			}
		}

		const nbItemToDisplay = Math.min(this.state.page * DRIVE_ITEM_LIST_PAGE_SIZE, driveItemIdsToDisplay.length);

		driveItemIdsToDisplay
			.slice(0, nbItemToDisplay)
			.forEach(driveItemId => {
				if (!storeDriveItems[driveItemId]) {
					dispatch(fetchDriveItem(driveItemId, folderId));
				}
			});

		// display all drive items in Coverflow mode in mobile app (as all items are already fetched)
		const shouldDisplayAllDriveItems = UserAgentUtils.isMobileApp && displayType === ListDisplayType.Coverflow;

		if (shouldDisplayAllDriveItems && nbItemToDisplay > 0 && nbItemToDisplay !== driveItemIdsToDisplay.length) {
			const pageCount = Math.ceil(driveItemIdsToDisplay.length / DRIVE_ITEM_LIST_PAGE_SIZE);
			this.setState({ page: pageCount });
		}
	}

	componentWillUnmount() {
		this.resetEditMode();
	}

	resetEditMode() {
		const { isEditMode, dispatch } = this.props;
		if (isEditMode) {
			dispatch(toggleEditMode());
		}
	}

	render() {
		const {
			displayType,
			driveItemIdsToDisplay,
			isFetching,
			showUnreadBadge,
			navigationItems,
			hasBottomBar,
			title,
			showSearchField,
			hasActions,
			hasSelectionIcon,
			onDriveItemClick,
			sharedDirId,
			isEditMode,
			isEditButtonDisplayed,
			isDisplayTypeDropdownDisplayed = true,
			isPrivateDir,
			isLoading,
			isFilesDndEnabled,
			shouldDisplayFilesToast,
			folderId,
			dispatch,
			storeDriveItems,
		} = this.props;
		const { page } = this.state;
		const { driveItemsToDisplay } = this;
		const isMasonry = displayType === ListDisplayType.Masonry;
		const isCoverFlow = displayType === ListDisplayType.Coverflow;
		const isLoadingNewItems = (driveItemsToDisplay.length < page * DRIVE_ITEM_LIST_PAGE_SIZE)
		&& driveItemsToDisplay.length < driveItemIdsToDisplay.length;
		const hasMoreDriveItems = this.hasMoreDriveItems();
		const driveItemsElement = driveItemsToDisplay.map((driveItem: Api.DriveItem, index: number) => (
			<DriveItem
				key={driveItem.id}
				driveItem={driveItem}
				// to skip index of SHARED_DIR
				// TODO: to think for better approach in case some other 'system resources' will be added
				index={isPrivateDir ? index - 1 : index}
				showUnreadBadge={showUnreadBadge}
				displayType={displayType}
				hasActions={hasActions}
				hasSelectionIcon={hasSelectionIcon}
				isDraggable={driveItem.id !== sharedDirId}
				itemsCount={navigationItems.length + driveItemsToDisplay.length}
				onClick={onDriveItemClick
					? () => onDriveItemClick(driveItem)
					: undefined}
			/>
		));

		const listItemsElement = navigationItems.concat(driveItemsElement);
		const isEmpty = listItemsElement.length + driveItemIdsToDisplay.length === 0;

		const infiniteScrollParams = {
			loadMore: this.loadMoreDriveItems,
			hasMore: !isLoadingNewItems && hasMoreDriveItems,
			useWindow: false,
		};

		return (
			<DndProvider backend={MultiBackend} options={HTML5toTouch}>
				{isEditMode && (
					<>
						<DesktopDragPreview displayType={displayType} />
						<TouchDragPreview />
						<DragScroller
							position={DragScrollerPosition.top}
							container={this.driveItemListElement}
						/>
						<DragScroller
							position={DragScrollerPosition.bottom}
							container={this.driveItemListElement}
						/>
					</>
				)}
				<DriveItemListContainer
					isEmpty={isEmpty}
					hasBottomBar={hasBottomBar}
				>
					<TitleWrapper>
						{title && !showSearchField &&
							<Title title={title} />
						}
						{!isEmpty &&
							<ButtonsWrapper>
								{isEditButtonDisplayed && !isMasonry && !isCoverFlow &&
									<ButtonEdit
										type="button"
										isEditMode={isEditMode}
										onClick={() => dispatch(toggleEditMode())}
									>
										<Translate value={isEditMode ? 'messages.done' : 'messages.move'} />
									</ButtonEdit>
								}
								{isDisplayTypeDropdownDisplayed &&
									<DisplayTypeDropdown
										folderId={folderId}
										displayType={displayType}
										dispatch={dispatch}
									/>
								}
							</ButtonsWrapper>
						}
					</TitleWrapper>
					<StyledDriveItemList
						ref={element => { this.driveItemListElement = element; }}
						showSearchField={showSearchField}
						isEditMode={isEditMode}
						isCoverFlow={isCoverFlow}
					>
						{isEmpty && !isLoadingNewItems && !isFetching
							? <div />
							: isCoverFlow
								? (
									<CoverFlow
										// key is used to reinitialize slider when change a folder
										key={folderId}
										folderId={folderId}
										idsToDisplay={driveItemIdsToDisplay}
										driveItems={storeDriveItems}
										navigationItemsLength={navigationItems.length}
										onFolderChange={this.handleCoverFlowFolderChange}
										onSlideChange={this.handleCoverFlowSlideChange}
									>
										{listItemsElement}
									</CoverFlow>
								)
								: (
									<StyledInfiniteScroll {...infiniteScrollParams}>
										{isMasonry
											? <Masonry>{listItemsElement}</Masonry>
											: listItemsElement
										}
									</StyledInfiniteScroll>
								)
						}
						{isFetching && hasMoreDriveItems && !isCoverFlow &&
							<LoadingStatus>
								{Utils.getSvgIcon('iconset_loading')}
								<Translate value="messages.loading" />
							</LoadingStatus>
						}
						{isLoading &&
							<Loader>
								{Utils.getSvgIcon('iconset_loading')}
								<Translate value="messages.loading" />
							</Loader>
						}
					</StyledDriveItemList>
				</DriveItemListContainer>
				{!UserAgentUtils.isMobileApp && isFilesDndEnabled && <FilesDndUploader />}
				{shouldDisplayFilesToast && <FilesToast />}
			</DndProvider>
		);
	}

	private hasMoreDriveItems(): boolean {
		const { driveItemIdsToDisplay } = this.props;
		const pageCount = Math.ceil(driveItemIdsToDisplay.length / DRIVE_ITEM_LIST_PAGE_SIZE);
		return this.state.page < pageCount;
	}

	private loadMoreDriveItems(): void {
		const page = this.state.page + 1;
		this.setState({ page });
	}

	private get driveItemsToDisplay(): Api.DriveItem[] {
		const { driveItemIdsToDisplay, storeDriveItems } = this.props;
		const nbItemToDisplay = Math.min(this.state.page * DRIVE_ITEM_LIST_PAGE_SIZE, driveItemIdsToDisplay.length);

		return driveItemIdsToDisplay
			.slice(0, nbItemToDisplay)
			.map(driveItemId => storeDriveItems[driveItemId])
			.filter(driveItem => !!driveItem);
	}

	private handleCoverFlowFolderChange(): void {
		this.setState({ page: 1 });
	}

	private handleCoverFlowSlideChange(newIndex: number): void {
		const { navigationItems } = this.props;
		const displayedItemsLength = navigationItems.length + this.driveItemsToDisplay.length;

		if (this.hasMoreDriveItems() && newIndex === displayedItemsLength - 1) {
			this.loadMoreDriveItems();
		}
	}
}

export default DriveItemList;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/DriveItemList/index.tsx